import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaTrophy } from "react-icons/fa";
import { UserContext } from "../contexts/UserContext";
import Sidebar from "../common/Sidebar";
import Header from "./common/Header";
import MobileTopbar from "../common/MobileTopbar";
import MobileSidebarDrawer from "../common/MobileSidebarDrawer";
import {
  JOURNEY_MODAL_FLAG,
  ONBOARDING_CHECKLIST,
} from "../constants/storageKeys";
import { SyllabusContext } from "../contexts/SyllabusContext";

const DEFAULT_SUBJECTS = ["Mathematics", "Physics", "Chemistry", "Biology"];

const JourneyModal = ({ open, onClose, onNavigate, checklist }) => {
  const { userData } = useContext(UserContext);

  const showSelfAssessment = checklist?.self_assessment === false;
  const showEvaluatedSheet = checklist?.evaluated_answersheet === false;
  const hasVisibleSteps = showSelfAssessment || showEvaluatedSheet;

  if (!open || !hasVisibleSteps) return null;

  const userName = userData?.name?.trim() || "Mathlete";
  const gridColumns =
    showSelfAssessment && showEvaluatedSheet
      ? "sm:grid-cols-2"
      : "sm:grid-cols-1";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8 backdrop-blur-sm">
      <div className="relative w-full max-w-xl rounded-3xl bg-white p-8 shadow-2xl">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 text-slate-500 transition hover:bg-slate-100"
        >
          <span className="text-lg leading-none">X</span>
        </button>
        <div className="space-y-4">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
            Welcome!
          </p>
          <h2 className="text-3xl font-bold text-slate-900">
            Start your journey - {userName}!
          </h2>
          <p className="text-base text-slate-600">
            Help Aanya learn how you study so she can plan practice made just
            for you. Choose one quick step to share a little more.
          </p>
          <div className={`grid gap-4 ${gridColumns}`}>
            {showSelfAssessment && (
              <button
                type="button"
                onClick={() => onNavigate("/assessment")}
                className="flex w-full flex-col gap-2 rounded-2xl bg-primary p-5 text-left text-white shadow-lg shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary-dark"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80"></p>
                <p className="text-xl font-semibold">Self Assessment</p>
                <p className="text-sm text-white/90">
                  - a short check-up so Aanya knows where to start.
                </p>
              </button>
            )}
            {showEvaluatedSheet && (
              <button
                type="button"
                onClick={() => onNavigate("/practice/evaluateAnswerSheet")}
                className="flex w-full flex-col gap-2 rounded-2xl bg-primary p-5 text-left text-white shadow-lg shadow-slate-400/40 transition hover:-translate-y-0.5 hover:bg-slate-800"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70"></p>
                <p className="text-xl font-semibold">Upload Answer Sheet</p>
                <p className="text-sm text-white/85">
                  - share your recent answers so we can tailor the next plan.
                </p>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-slate-500 underline-offset-4 hover:underline"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

const NewHomeComponent = ({ forceShowNewUI }) => {
  const { userData } = useContext(UserContext);
  const syllabusContext = useContext(SyllabusContext);
  const { subjects: syllabusSubjects } = syllabusContext || {};
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [journeyChecklist, setJourneyChecklist] = useState(null);

  // Flag to toggle between UI versions - change this to true to show NewHomeComponent2 UI
  const [showNewUI, setShowNewUI] = useState(
    typeof forceShowNewUI === "boolean" ? forceShowNewUI : true
  );

  // Subjects sourced from syllabus context (fallback to defaults)
  const subjects =
    Array.isArray(syllabusSubjects) && syllabusSubjects.length > 0
      ? syllabusSubjects
      : [];
  const [selectedSubject, setSelectedSubject] = useState(
    subjects[0] || DEFAULT_SUBJECTS[0]
  );

  // Sample mission data - in real app, this would come from API/context
  const missionData = {
    title: "Linear Equations",
    subtitle: "My active mission",
    progress: 75,
    mastery: 75,
  };

  const userName = userData?.name?.trim() || "Student";
  const profilePhoto =
    userData?.profilePhoto || "/assets/avatars/defaultprofile.png";

  const handleSolveDoubt = () => {
    navigate("/tutor");
  };

  const handleCheckWork = () => {
    navigate("/practice/evaluateAnswerSheet?mode=new");
  };

  const handleGoToLearn = () => {
    navigate("/practice");
  };

  useEffect(() => {
    console.log(syllabusSubjects);
    const availableSubjects =
      Array.isArray(syllabusSubjects) && syllabusSubjects.length > 0
        ? syllabusSubjects
        : DEFAULT_SUBJECTS;

    if (!availableSubjects.length) return;

    setSelectedSubject((current) =>
      current && availableSubjects.includes(current)
        ? current
        : availableSubjects[0]
    );
  }, [syllabusSubjects]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const readChecklist = () => {
      try {
        const rawChecklist = localStorage.getItem(ONBOARDING_CHECKLIST);
        return rawChecklist ? JSON.parse(rawChecklist) : null;
      } catch (error) {
        console.error("Unable to parse onboarding checklist:", error);
        return null;
      }
    };

    const evaluateJourneyModalState = async () => {
      const checklist = readChecklist();
      setJourneyChecklist(checklist);

      const hasPendingSteps =
        checklist?.self_assessment === false ||
        checklist?.evaluated_answersheet === false;
      // const shouldShowFlag =
      //   (await localStorage.getItem(JOURNEY_MODAL_FLAG)) === "true";
      const shouldShowFlag = true; // Always show for testing
      if (typeof forceShowNewUI === "boolean") {
        setShowNewUI(forceShowNewUI);
        if (!hasPendingSteps) {
          localStorage.setItem(JOURNEY_MODAL_FLAG, "false");
        }
      } else if (hasPendingSteps && shouldShowFlag) {
        setShowNewUI(false);
        localStorage.setItem(JOURNEY_MODAL_FLAG, "false");
      } else {
        setShowNewUI(true);
        if (!hasPendingSteps) {
          localStorage.setItem(JOURNEY_MODAL_FLAG, "false");
        }
      }
    };

    evaluateJourneyModalState();
    const recheckJourneyState = () => evaluateJourneyModalState();
    window.addEventListener("userLoggedIn", recheckJourneyState);
    window.addEventListener("storage", recheckJourneyState);

    return () => {
      window.removeEventListener("userLoggedIn", recheckJourneyState);
      window.removeEventListener("storage", recheckJourneyState);
    };
  }, [forceShowNewUI]);

  const markJourneyModalHandled = () => {
    try {
      localStorage.setItem(JOURNEY_MODAL_FLAG, "false");
    } catch (error) {
      console.error("Unable to update journey modal flag:", error);
    }
  };

  const handleJourneyModalClose = () => {
    setShowJourneyModal(false);
    markJourneyModalHandled();
  };

  const handleJourneyModalNavigate = (path) => {
    handleJourneyModalClose();
    navigate(path);
  };

  // Shield icons with different states (for NewHomeComponent2 UI)
  const ShieldIcon = ({ type, position, progress }) => {
    const threshold = (position - 1) * 25;
    const isUnlocked = progress >= threshold;

    let fillColor = "#E5E7EB"; // Grey - locked
    let iconColor = "#9CA3AF";
    let strokeColor = "#D1D5DB";

    if (type === "first") {
      fillColor = isUnlocked ? "#F97316" : "#E5E7EB"; // Orange when unlocked
      iconColor = isUnlocked ? "#FFFFFF" : "#9CA3AF";
      strokeColor = isUnlocked ? "#FFFFFF" : "#D1D5DB";
    } else if (type === "second") {
      fillColor = isUnlocked ? "#6B7280" : "#E5E7EB"; // Grey with stars when unlocked
      iconColor = isUnlocked ? "#FCD34D" : "#9CA3AF"; // Yellow stars
      strokeColor = isUnlocked ? "#FFFFFF" : "#D1D5DB";
    } else if (type === "third") {
      fillColor = isUnlocked ? "#6B7280" : "#E5E7EB"; // Grey with airplane when unlocked
      iconColor = isUnlocked ? "#FFFFFF" : "#9CA3AF";
      strokeColor = isUnlocked ? "#FFFFFF" : "#D1D5DB";
    } else if (type === "fourth") {
      fillColor = isUnlocked ? "#FCD34D" : "#E5E7EB"; // Yellow when unlocked
      iconColor = isUnlocked ? "#FFFFFF" : "#9CA3AF";
      strokeColor = isUnlocked ? "#FFFFFF" : "#D1D5DB";
    }

    // Position shields at 0%, 25%, 50%, and 100%
    const positions = [0, 25, 50, 100];
    const leftPosition = positions[position - 1];

    return (
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10"
        style={{ left: `${leftPosition}%` }}
      >
        <svg
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-8 h-8 md:w-9 md:h-9"
        >
          {/* Shield shape */}
          <path
            d="M18 5L8 9V18C8 24 12 28 18 30C24 28 28 24 28 18V9L18 5Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="1.5"
          />
          {/* Icon content based on type */}
          {type === "first" && isUnlocked && (
            <circle cx="18" cy="18" r="4" fill={iconColor} />
          )}
          {type === "second" && isUnlocked && (
            <>
              <path
                d="M18 13L19.5 16.5L23 17L20 19.5L20.5 23L18 21.5L15.5 23L16 19.5L13 17L16.5 16.5L18 13Z"
                fill={iconColor}
              />
              <circle cx="11" cy="11" r="1.5" fill={iconColor} />
              <circle cx="25" cy="11" r="1.5" fill={iconColor} />
            </>
          )}
          {type === "third" && isUnlocked && (
            <>
              <path
                d="M9 18L18 13L27 18L22 21L18 19L14 21L9 18Z"
                fill={iconColor}
              />
              <circle cx="11" cy="11" r="1.5" fill={iconColor} />
              <circle cx="25" cy="11" r="1.5" fill={iconColor} />
            </>
          )}
          {type === "fourth" && isUnlocked && (
            <path
              d="M18 11L20 15L24 16L21 19L21.5 23L18 21L14.5 23L15 19L12 16L16 15L18 11Z"
              fill={iconColor}
            />
          )}
        </svg>
      </div>
    );
  };

  return (
    <>
      <div className="h-[100dvh] flex overflow-hidden bg-white">
        {/* Sidebar - shown only on md+ screens */}
        <div className="hidden md:block">
          <Sidebar />
        </div>

        {/* Mobile Drawer */}
        <MobileSidebarDrawer
          open={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile Topbar */}
          <MobileTopbar
            onMenuClick={() => setIsMobileMenuOpen(true)}
            showMenuButton={true}
            showProfileButton={false}
            showEditButton={false}
          />
          {/* Hide Header only on assessment and practice pages; show on dashboard (compact) */}

          <div>
            <Header />
            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6 md:py-12 md:flex md:items-center">
              <div className="max-w-4xl mx-auto w-full">
                {showNewUI ? (
                  /* NewHomeComponent2 UI - Mission Card with Progress */
                  <>
                    {/* Subject Navigation Tabs */}
                    <div className="mb-6 md:mb-8 w-full flex-row justify-center align-center">
                      <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 -mx-2 px-2 md:mx-0 md:px-0 align-center justify-center">
                        {subjects.map((subject) => (
                          <button
                            key={subject}
                            onClick={() => setSelectedSubject(subject)}
                            className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                              selectedSubject === subject
                                ? "bg-[#0D99FF] text-white shadow-md"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {subject}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Mission Card */}
                    <div className="bg-white rounded-2xl md:rounded-3xl shadow-lg border border-gray-100 p-8 md:p-10 lg:p-12">
                      {/* Title and Subtitle */}
                      <div className="text-center mb-8 md:mb-10">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
                          {missionData.title}
                        </h2>
                        <p className="text-base md:text-lg text-gray-500">
                          {missionData.subtitle}
                        </p>
                      </div>

                      {/* Progress Bar Container */}
                      <div className="relative mb-10 md:mb-12">
                        {/* Background Progress Bar */}
                        <div className="relative h-10 md:h-12 bg-gray-200 rounded-full overflow-hidden">
                          {/* Filled Progress */}
                          <div
                            className="absolute top-0 left-0 h-full bg-[#0D99FF] rounded-full transition-all duration-500"
                            style={{ width: `${missionData.progress}%` }}
                          />
                          {/* Progress Text - positioned on the blue part */}
                          <div
                            className="absolute top-0 left-0 h-full flex items-center justify-center"
                            style={{ width: `${missionData.progress}%` }}
                          >
                            <span className="text-sm md:text-base font-semibold text-white z-10">
                              {missionData.mastery}% Mastery
                            </span>
                          </div>
                        </div>

                        {/* Shield Icons - positioned along the progress bar */}
                        <div className="relative -mt-6 md:-mt-7">
                          <ShieldIcon
                            type="first"
                            position={1}
                            progress={missionData.progress}
                          />
                          <ShieldIcon
                            type="second"
                            position={2}
                            progress={missionData.progress}
                          />
                          <ShieldIcon
                            type="third"
                            position={3}
                            progress={missionData.progress}
                          />
                          <ShieldIcon
                            type="fourth"
                            position={4}
                            progress={missionData.progress}
                          />
                        </div>
                      </div>

                      {/* Check My Work Button */}
                      <div className="flex justify-center">
                        <button
                          onClick={handleCheckWork}
                          className="w-full md:w-auto px-10 md:px-14 py-4 md:py-5 bg-[#0D99FF] text-white rounded-xl md:rounded-2xl text-base md:text-lg font-semibold hover:bg-[#6351E0] transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
                        >
                          Check My Work
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Original NewHomeComponent UI - Welcome with Action Cards */
                  <>
                    {/* Welcome Section */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 md:w-16 md:h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                        <img
                          src={profilePhoto}
                          alt={userName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = "/assets/avatars/defaultprofile.png";
                          }}
                        />
                      </div>
                      <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                          Hi, {userName}!
                        </h1>
                        <p className="text-sm md:text-base text-gray-500 mt-1">
                          What would you like to do first?
                        </p>
                      </div>
                    </div>

                    {/* Action Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-8 md:mt-12">
                      {/* Solve My Doubt Card */}
                      <button
                        onClick={handleSolveDoubt}
                        className="group relative bg-white border-2 border-blue-500 rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
                      >
                        <div className="flex flex-col items-center text-center">
                          {/* Magic Wand Icon */}
                          <div className="mb-4 md:mb-6">
                            <svg
                              width="80"
                              height="80"
                              viewBox="0 0 80 80"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-16 h-16 md:w-20 md:h-20"
                            >
                              {/* Wand handle */}
                              <rect
                                x="35"
                                y="50"
                                width="10"
                                height="20"
                                fill="#2C2C2C"
                                rx="2"
                              />
                              {/* Wand tip */}
                              <path
                                d="M40 20L45 35L60 40L45 45L40 60L35 45L20 40L35 35L40 20Z"
                                fill="#2C2C2C"
                              />
                              {/* Stars */}
                              <circle cx="25" cy="25" r="3" fill="#FFD700" />
                              <circle cx="55" cy="25" r="3" fill="#FFD700" />
                              <circle cx="25" cy="55" r="3" fill="#FFD700" />
                              <circle cx="55" cy="55" r="3" fill="#FFD700" />
                              <circle cx="40" cy="15" r="2" fill="#FFD700" />
                              <circle cx="15" cy="40" r="2" fill="#FFD700" />
                              <circle cx="65" cy="40" r="2" fill="#FFD700" />
                            </svg>
                          </div>
                          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                            SOLVE MY DOUBT
                          </h2>
                          <p className="text-sm md:text-base text-gray-500">
                            Point camera or upload assignment
                          </p>
                        </div>
                      </button>

                      {/* Check My Work Card */}
                      <button
                        onClick={handleCheckWork}
                        className="group relative bg-white border-2 border-orange-500 rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
                      >
                        <div className="flex flex-col items-center text-center">
                          {/* Trophy Icon */}
                          <div className="mb-4 md:mb-6">
                            <FaTrophy
                              className="w-16 h-16 md:w-20 md:h-20"
                              style={{ color: "#FFD700" }}
                            />
                          </div>
                          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                            CHECK MY WORK
                          </h2>
                          <p className="text-sm md:text-base text-gray-500">
                            Point camera or upload your worksheet
                          </p>
                        </div>
                      </button>
                    </div>

                    {/* Learn Tab Link */}
                    <div className="mt-8 md:mt-12 text-center">
                      <button
                        onClick={handleGoToLearn}
                        className="text-sm md:text-base text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Just exploring?{" "}
                        <span className="underline">Go to Learn Tab.</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewHomeComponent;
